import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import TCClient from '../../../../client';

import BEM from '../../../../common/bem';
import { ReactComponent as DndStatusSvg } from '../../../../common/images/dnd-status.svg';
import { ReactComponent as ArrowDownSvg } from '../../../../common/images/arrow-down.svg';
import { ReactComponent as ArrowUpSvg } from '../../../../common/images/arrow-up.svg';
import { ReactComponent as AddMemberSvg } from '../../../../common/images/add-member.svg';
import { ReactComponent as AddSuccessSvg } from '../../../../common/images/add-success.svg';
import { ReactComponent as AddLoadingSvg } from '../../../../common/images/add-loading.svg';
import { Group, GroupTypes, User, Conversation } from '../../../../types';
import { EntityAvatar, UserName, UserTitleDepartment } from '../../../../common/components';
import { mobxInjectSelect } from '../../../../common/utils';

const classes = BEM.with('DelegationBanner');

type AutoForwardInfo = {
  name: string;
  autoForwardReceiverIds: string[];
};

export const DelegationText = ({ delegationText }: { delegationText: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      const hasOverflow = ref.current.scrollHeight > ref.current.clientHeight;
      setIsOverflowing(hasOverflow);
    }
    setIsExpanded(false);
  }, [delegationText]);

  const handleClick = () => {
    if (isOverflowing || isExpanded) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      ref={ref}
      onClick={handleClick}
      className={classes('delegation-text', { isExpanded, isOverflowing })}
      title={delegationText}
      data-test-id="delegation-text"
    >
      {delegationText}
    </div>
  );
};

type AddMemberState = 'none' | 'can-add' | 'already-added';

const getAddMemberState = (
  autoForward: AutoForwardInfo | undefined,
  memberIds: string[]
): { state: AddMemberState; receiverId?: string } => {
  if (!autoForward) return { state: 'none' };

  const hasMultipleRecipients = autoForward.autoForwardReceiverIds.length > 1;
  if (hasMultipleRecipients) return { state: 'none' };

  const receiverId = autoForward.autoForwardReceiverIds[0];
  const isAlreadyMember = memberIds.includes(receiverId);

  return {
    state: isAlreadyMember ? 'already-added' : 'can-add',
    receiverId,
  };
};

const AutoForwardText = ({
  autoForward,
  memberId,
  memberIds,
  addMembers,
  groupId,
  groupType,
}: {
  autoForward?: AutoForwardInfo;
  memberId?: string;
  memberIds?: string[];
  addMembers: (groupId: string, memberIds: string[]) => Promise<void>;
  groupId?: string;
  groupType?: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!autoForward?.name) return null;

  const hasMultipleRecipients = autoForward.autoForwardReceiverIds.length > 1;
  const addMemberInfo = getAddMemberState(autoForward, memberIds || []);

  const handleAddMember = async () => {
    if (!groupId || !addMemberInfo.receiverId) return;

    setIsLoading(true);
    await addMembers(groupId, [addMemberInfo.receiverId]);
    setIsLoading(false);
  };

  const handleViewRecipients = () => {};

  return (
    <div className={classes('auto-forward-text')}>
      <div className={classes('auto-forward-text-container')}>
        <span data-test-id={`${memberId}-auto-forwarding-to`}>
          Auto-forwarding to: {autoForward.name}
        </span>
        {hasMultipleRecipients && (
          <a
            href="#"
            className={classes('auto-forward-text-view')}
            data-test-id={`${memberId}-view`}
            onClick={handleViewRecipients}
          >
            View
          </a>
        )}
        {isLoading && (
          <AddLoadingSvg
            className={classnames(
              classes('auto-forward-text-icon'),
              classes('auto-forward-text-icon--spinning')
            )}
            data-test-id={`${memberId}-add-loading`}
          />
        )}
        {!isLoading && addMemberInfo.state === 'can-add' && groupType === GroupTypes.GROUP && (
          <AddMemberSvg
            className={classnames(
              classes('auto-forward-text-icon'),
              classes('auto-forward-text-icon--pointer')
            )}
            data-test-id={`${memberId}-add-member`}
            onClick={handleAddMember}
          />
        )}
        {!isLoading && addMemberInfo.state === 'already-added' && (
          <AddSuccessSvg
            className={classes('auto-forward-text-icon')}
            data-test-id={`${memberId}-add-success`}
          />
        )}
      </div>
    </div>
  );
};

type DelegationBannerProps = {
  addMembers: (groupId: string, memberIds: string[]) => Promise<void>;
  composeToEntityV2: (entity: User) => void;
  currentConversation: Conversation;
  currentOrganizationId: string;
  currentUser: User;
  currentUserId: string;
  getAutoForwardEntityDisplayName: (entities: string[]) => string;
  initializeGroupEditor: ({ group }: { group: Group }) => void;
  isProviderNetwork: boolean;
  isUserDndAfEnabledForCurrentOrg: (user: User) => boolean;
  subscribeUser: (userId: string) => void;
  syncGroupMembers: () => void;
  unsubscribeUser: (userId: string) => void;
};

async function fetchTeamMembers(
  teamToken: string,
  organizationId: string
): Promise<string[] | null> {
  try {
    const group = await TCClient.teams.find(teamToken, { organizationId });
    return group.members.map((member: User) => member.id);
  } catch (error) {
    console.error('Failed to fetch group members:', error);
    return null;
  }
}

async function processAutoForwardEntities(
  profile: {
    autoForwardReceiverIds?: string[];
    dndAutoForwardEntities?: unknown;
  },
  getDisplayName: (entities: unknown) => string,
  currentOrgId: string
): Promise<AutoForwardInfo | undefined> {
  const { autoForwardReceiverIds, dndAutoForwardEntities } = profile;

  if (autoForwardReceiverIds && autoForwardReceiverIds.length > 0) {
    const name = getDisplayName(dndAutoForwardEntities || []);
    return { name, autoForwardReceiverIds: [autoForwardReceiverIds[0]] };
  }

  if (
    !dndAutoForwardEntities ||
    !Array.isArray(dndAutoForwardEntities) ||
    dndAutoForwardEntities.length === 0
  ) {
    return undefined;
  }

  const firstEntity = dndAutoForwardEntities[0] as {
    entityType: string;
    displayName: string;
    token: string;
  };

  if (firstEntity.entityType === 'account') {
    const receiverIds = (
      dndAutoForwardEntities as Array<{
        token: string;
      }>
    ).map((entity) => entity.token);
    return {
      name: 'Multiple recipients',
      autoForwardReceiverIds: receiverIds,
    };
  }

  if (firstEntity.entityType === 'team') {
    const teamMemberIds = await fetchTeamMembers(firstEntity.token, currentOrgId);
    if (teamMemberIds) {
      return {
        name: firstEntity.displayName,
        autoForwardReceiverIds: teamMemberIds,
      };
    }
  }

  return undefined;
}

const DelegationBannerComponent = ({
  addMembers,
  composeToEntityV2,
  currentConversation,
  currentOrganizationId,
  currentUser,
  currentUserId,
  getAutoForwardEntityDisplayName,
  initializeGroupEditor,
  isProviderNetwork,
  isUserDndAfEnabledForCurrentOrg,
  subscribeUser,
  syncGroupMembers,
  unsubscribeUser,
}: DelegationBannerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [autoForwardData, setAutoForwardData] = useState<Array<AutoForwardInfo | undefined>>([]);
  const [isLoadingAutoForward, setIsLoadingAutoForward] = useState(false);

  const group = currentConversation?.counterParty as Group;

  const members = useMemo(() => group?.members || [], [group?.members]);
  const memberIds = members.map((member) => member.id) || [];

  const memberDndHash =
    members
      ?.map((m) => `${m.id}:${!!m.dnd}`)
      .sort()
      .join('|') || '';

  const unavailableMembers = useMemo(
    () => members?.filter((member) => member.dnd && member.id !== currentUserId) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberDndHash, currentUserId]
  );

  useEffect(() => {
    const allMembers = members || [];

    allMembers.forEach((member) => {
      if (member.id !== currentUserId) {
        subscribeUser(member.id);
      }
    });

    return () => {
      allMembers.forEach((member) => {
        if (member.id !== currentUserId) {
          unsubscribeUser(member.id);
        }
      });
    };
  }, [group?.id, members, currentUserId, subscribeUser, unsubscribeUser]);

  const fetchAutoForwardData = useCallback(
    async (members: User[]) => {
      const results = await Promise.all(
        members.map(async (member) => {
          const profile = member.profileByOrganizationId?.[currentOrganizationId];
          if (!profile) return undefined;

          return processAutoForwardEntities(
            profile as { autoForwardReceiverIds?: string[]; dndAutoForwardEntities?: unknown },
            getAutoForwardEntityDisplayName as (entities: unknown) => string,
            currentOrganizationId
          );
        })
      );
      return results;
    },
    [currentOrganizationId, getAutoForwardEntityDisplayName]
  );

  const refetchUserProfiles = useCallback(
    async (members: User[]) => {
      const userIds = members.map((member) => member.id);

      try {
        const freshUsers = await Promise.all(
          userIds.map((userId) =>
            TCClient.users.find(userId, {
              bypassCache: true,
              organizationId: currentOrganizationId,
            })
          )
        );

        return await fetchAutoForwardData(freshUsers);
      } catch (error) {
        console.error('Failed to refetch user profiles:', error);
        return [];
      }
    },
    [currentOrganizationId, fetchAutoForwardData]
  );

  const handleToggleBanner = useCallback(async () => {
    if (isLoadingAutoForward) return;

    const newExpandedState = !isExpanded;

    if (!newExpandedState) {
      setIsExpanded(false);
      return;
    }

    if (unavailableMembers.length > 0) {
      setIsLoadingAutoForward(true);
      const freshData = await refetchUserProfiles(unavailableMembers);
      setAutoForwardData(freshData);
      setIsLoadingAutoForward(false);
    }
    setIsExpanded(true);
  }, [isLoadingAutoForward, isExpanded, unavailableMembers, refetchUserProfiles]);

  useEffect(() => {
    setIsExpanded(false);
    if (group) {
      initializeGroupEditor({ group });
      syncGroupMembers();
    }
  }, [initializeGroupEditor, group, syncGroupMembers]);

  useEffect(() => {
    if (unavailableMembers.length > 0) {
      fetchAutoForwardData(unavailableMembers).then(setAutoForwardData);
    }
  }, [fetchAutoForwardData, unavailableMembers]);

  if (unavailableMembers.length === 0) return null;

  const bannerText =
    unavailableMembers.length === 1
      ? '1 person in this group is unavailable'
      : `${unavailableMembers.length} people in this group are unavailable`;

  return (
    <div className={classes()} data-test-id="delegation-banner">
      <div className={classes('delegation-status')}>
        <div className={classes('delegation-status-information')}>
          <div className={classes('delegation-column-container')}>
            <div className={classes('delegation-svg')}>
              <DndStatusSvg />
            </div>
            <DelegationText delegationText={bannerText} />
          </div>

          <div
            className={classes('delegation-column-container')}
            onClick={handleToggleBanner}
            data-test-id="delegation-banner-toggle-details"
          >
            <span>{isExpanded ? 'Hide details' : 'Show details'}</span>
            <div className={classnames(classes('delegation-svg'), classes('delegation-svg-arrow'))}>
              {isExpanded ? <ArrowUpSvg /> : <ArrowDownSvg />}
            </div>
          </div>
        </div>

        {isExpanded &&
          unavailableMembers.map((member, index) => {
            const isCurrentUser = member.id === currentUserId;
            const isRoleBot = currentUser?.roles?.some((role) => role.botUserId === member.id);
            const isMemberClickable = isProviderNetwork && !isCurrentUser && !isRoleBot;

            const handleMemberClick = () => {
              if (isMemberClickable) {
                composeToEntityV2(member);
              }
            };

            const isDndAfEnabled = isUserDndAfEnabledForCurrentOrg(member);

            return (
              <div className={classes('delegation-unavailable-members-container')} key={member.id}>
                <div className={classes('delegation-unavailable-members-container-separator')} />
                <div className={classes('delegation-unavailable-members-container-content')}>
                  <div
                    className={classes('member-info', {
                      clickable: isMemberClickable,
                    })}
                    onClick={handleMemberClick}
                    data-test-id={`delegation-banner-individual-${member.id}`}
                  >
                    <EntityAvatar
                      entity={member.isRoleBot && member.botRole ? member.botRole : member}
                      size={30}
                      showPresenceIndicator
                    />
                    <div className={classes('member-details')}>
                      <UserName
                        className={classes('member-name', {
                          dndAf: isDndAfEnabled,
                        })}
                        includeRoleOwner
                        isCurrentUser={isCurrentUser}
                        user={member}
                      />
                      {member.dndText && (
                        <div className={classes('member-dnd-text')}>{member.dndText}</div>
                      )}
                      <UserTitleDepartment className={classes('member-title')} user={member} />
                    </div>
                  </div>
                  <AutoForwardText
                    autoForward={autoForwardData[index]}
                    memberId={member.id}
                    memberIds={memberIds}
                    addMembers={addMembers}
                    groupId={group?.id}
                    groupType={group?.groupType}
                  />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export const DelegationBanner = mobxInjectSelect<{}, DelegationBannerProps>({
  composeMessageStore: ['composeToEntityV2'],
  conversationStore: ['currentConversation'],
  groupEditorStore: ['initializeGroupEditor', 'syncGroupMembers'],
  groupStore: ['addMembers'],
  messengerStore: ['currentOrganizationId'],
  networkStore: ['isProviderNetwork'],
  presenceStore: ['subscribeUser', 'unsubscribeUser'],
  sessionStore: ['currentUser', 'currentUserId'],
  userStore: ['getAutoForwardEntityDisplayName', 'isUserDndAfEnabledForCurrentOrg'],
})(observer(DelegationBannerComponent));
